import {
  createUiMessages,
  ModalProvider,
  NotificationProvider,
  UiMessagesProvider,
} from '@smartsash/ui';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface UiProvidersProps {
  children: ReactNode;
}

/**
 * Mounts the `@smartsash/ui` runtime seams (HS-1170).
 *
 * Lives outside `App.tsx` because `react-refresh/only-export-components` wants a module
 * to export components alone, and `App.tsx` already carries the router wiring.
 *
 * Nesting is load-bearing:
 * - `NotificationProvider` reads its toast region's accessible name via `useUiMessages()`,
 *   so it has to sit inside `UiMessagesProvider`.
 * - `ModalProvider` owns the shared backdrop, focus trap and scroll lock for `openModal()`.
 *   Nothing calls it yet; it is mounted now so the imperative seam exists before the
 *   `ConfirmModal` slice needs it.
 *
 * `createUiMessages` adapts admin's `t` to the package's `ui.*` keys, which live in the
 * shared `common` namespace (`packages/i18n/locales/{en,fr}/common.json`) — not in admin's
 * own `app` namespace, hence the explicit namespace argument.
 */
export function UiProviders({ children }: UiProvidersProps) {
  const { t } = useTranslation('common');
  const messages = useMemo(() => createUiMessages(t), [t]);

  return (
    <UiMessagesProvider value={messages}>
      <NotificationProvider>
        <ModalProvider>{children}</ModalProvider>
      </NotificationProvider>
    </UiMessagesProvider>
  );
}
